import { csvHeaderKey, parseCsvRows } from "./client-csv";

export interface ServiceImportRow {
  rowId: string;
  name: string;
  description?: string;
  price: number;
  durationMinutes: number;
  rebookingIntervalWeeks: number;
  isActive: boolean;
  intakeQuestions: string[];
}

export interface BookingHistoryImportRow {
  rowId: string;
  serviceName: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  scheduledAt: string;
  status: "completed" | "cancelled" | "no_show";
  customerNotes?: string;
  acquisitionSource:
    | "direct"
    | "instagram"
    | "kakao"
    | "naver"
    | "google"
    | "referral"
    | "waitlist"
    | "other";
}

function records(text: string): Array<Record<string, string>> {
  const rows = parseCsvRows(text);
  const header = rows.shift();
  if (!header) throw new Error("csv_empty");
  if (rows.length > 500) throw new Error("csv_too_many_rows");
  const keys = header.map(csvHeaderKey);
  return rows.map((cells) =>
    Object.fromEntries(keys.map((key, index) => [key, cells[index] || ""])),
  );
}

function value(row: Record<string, string>, ...keys: string[]) {
  for (const key of keys) {
    const found = row[csvHeaderKey(key)];
    if (found !== undefined) return found.trim();
  }
  return "";
}

export function parseServiceCsv(text: string): ServiceImportRow[] {
  return records(text).map((row, index) => {
    const name = value(row, "서비스명", "name");
    const price = Number(value(row, "가격", "price"));
    const durationMinutes = Number(
      value(row, "소요시간(분)", "durationMinutes", "duration"),
    );
    if (!name || !Number.isInteger(price) || !Number.isInteger(durationMinutes))
      throw new Error(`csv_invalid_service_row:${index + 2}`);
    const active = value(row, "활성", "isActive").toLowerCase();
    const interval = Number(value(row, "재예약주기(주)", "rebookingIntervalWeeks") || 6);
    return {
      rowId: String(index + 2),
      name,
      description: value(row, "설명", "description"),
      price,
      durationMinutes,
      rebookingIntervalWeeks: interval,
      isActive: !["0", "false", "아니오", "비활성"].includes(active),
      intakeQuestions: value(row, "상담질문", "intakeQuestions")
        .split(/[|\n]/)
        .map((item) => item.trim())
        .filter(Boolean),
    };
  });
}

const historyStatuses = new Set(["completed", "cancelled", "no_show"]);
const sources = new Set([
  "direct",
  "instagram",
  "kakao",
  "naver",
  "google",
  "referral",
  "waitlist",
  "other",
]);

export function parseBookingHistoryCsv(text: string): BookingHistoryImportRow[] {
  return records(text).map((row, index) => {
    const serviceName = value(row, "서비스명", "serviceName");
    const customerName = value(row, "고객명", "customerName");
    const customerPhone = value(row, "연락처", "customerPhone", "phone");
    const rawDate = value(row, "예약일시", "scheduledAt");
    const parsedDate = new Date(rawDate);
    const status = value(row, "상태", "status") || "completed";
    const source = value(row, "유입경로", "acquisitionSource") || "direct";
    if (
      !serviceName ||
      !customerName ||
      !customerPhone ||
      !Number.isFinite(parsedDate.getTime()) ||
      !historyStatuses.has(status) ||
      !sources.has(source)
    )
      throw new Error(`csv_invalid_booking_row:${index + 2}`);
    return {
      rowId: String(index + 2),
      serviceName,
      customerName,
      customerPhone,
      customerEmail: value(row, "이메일", "customerEmail"),
      scheduledAt: parsedDate.toISOString(),
      status: status as BookingHistoryImportRow["status"],
      customerNotes: value(row, "예약메모", "customerNotes"),
      acquisitionSource: source as BookingHistoryImportRow["acquisitionSource"],
    };
  });
}
